import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import {
  Select as ChakraSelect,
  type ChakraStylesConfig,
  type GroupBase,
  type GroupProps,
  type MultiValue,
  type SingleValue,
} from 'chakra-react-select';
import { useEffect, useState } from 'react';

import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import type { MetricOption } from '../PageConfigProvider/types/metric';
import { BaseSelectStyles } from './BaseSelectStyles';
import {
  deriveSelectedWithDisabled,
  getIsolationHint,
} from './utils/metric-isolation';
import { getAvailableMetrics } from './utils/metric-mapping';

// Extended type for selected metrics that can be disabled in UI
type SelectableMetricOption = MetricOption & {
  isDisabled?: boolean;
};
type MultiMetricSelectorProps = {
  item: PositionedItem;
  isDisabled?: boolean;
  isMulti?: boolean;
  /** Override default visibility of "Unit of Analysis" UI. */
  showUnitOfAnalysis?: boolean;
};

export const MultiMetricSelector = ({
  item,
  isDisabled = false,
  isMulti = true,
  showUnitOfAnalysis = true,
}: MultiMetricSelectorProps) => {
  const { primaryFilter } = useTabContentConfig();

  const primaryFilterId = primaryFilter?.id;
  const chartType = item?.plot?.chartType;

  const availableMetricOptions = getAvailableMetrics({
    primaryFilterId,
    chartType,
  });
  const { dispatch } = useTabContentConfig();

  const id = item?.id;

  const { defaultMetricOptions } = item?.plot ?? { defaultMetricOptions: [] };

  // Unit of Analysis: 'primary' (Company/Geography/Role) or 'employees'
  type UnitOfAnalysis = 'primary' | 'employees';
  type UnitOption = { label: string; value: UnitOfAnalysis };
  const [unit, setUnit] = useState<UnitOfAnalysis>(() => {
    if (!showUnitOfAnalysis) return 'primary';
    return (defaultMetricOptions || []).includes('td_users')
      ? 'employees'
      : 'primary';
  });
  const effectiveUnit: UnitOfAnalysis = showUnitOfAnalysis ? unit : 'primary';

  // Derive label for primary option based on page primary filter
  const primaryLabel = (() => {
    switch (primaryFilterId) {
      case 'COMPANY':
        return 'Company';
      case 'GEOGRAPHY':
        return 'Geography';
      case 'ROLE':
        return 'Role';
      default:
        return 'Primary';
    }
  })();

  const unitOptions: UnitOption[] = [
    { label: primaryLabel, value: 'primary' },
    { label: 'Employees', value: 'employees' },
  ];

  // Adjust available metrics based on unit selection
  const adjustedAvailableMetricOptions = (() => {
    if (effectiveUnit === 'employees') return availableMetricOptions;
    // When primary is selected, hide the Employees metric ('td_users') from the list
    return (availableMetricOptions || []).map((group) => ({
      ...group,
      options: group.options.filter((opt) => opt.value !== 'td_users'),
    }));
  })();

  const unitSelected: UnitOption = (() => {
    const found = unitOptions.find((o) => o.value === effectiveUnit);
    return (
      found || {
        label: primaryLabel,
        value: 'primary',
      }
    );
  })();

  // When switching unit, sanitize selected metrics
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!showUnitOfAnalysis && unit !== 'primary') {
      setUnit('primary');
    }

    if (effectiveUnit === 'employees') {
      if (
        !defaultMetricOptions?.includes('td_users') ||
        defaultMetricOptions.length !== 1
      ) {
        dispatch({
          type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
          payload: { id, defaultMetricOptions: ['td_users'] },
        });
      }
    } else {
      if (defaultMetricOptions?.includes('td_users')) {
        const filtered = (defaultMetricOptions || []).filter(
          (m) => m !== 'td_users'
        );
        dispatch({
          type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
          payload: { id, defaultMetricOptions: filtered },
        });
      }
    }
  }, [effectiveUnit, showUnitOfAnalysis]);

  const selectedMetrics: SelectableMetricOption[] = (() => {
    if (
      defaultMetricOptions?.length === 1 &&
      defaultMetricOptions?.[0] === 'share_of_headcount'
    ) {
      return [
        {
          value: 'headcount',
          label: 'Headcount',
        },
      ];
    }
    const allOptions =
      adjustedAvailableMetricOptions?.flatMap((group) => group.options) || [];
    const selected = deriveSelectedWithDisabled(
      allOptions,
      defaultMetricOptions || []
    );

    if (selected?.length > 0) {
      return selected as SelectableMetricOption[];
    }

    return adjustedAvailableMetricOptions?.[0]?.options?.[0]
      ? [adjustedAvailableMetricOptions[0].options[0]]
      : [];
  })();

  const handleChangeMetrics = (metrics: MultiValue<SelectableMetricOption>) => {
    const newOptions = metrics.map((metric) => metric.value);

    // Preserve previous order for existing selections, append new ones to the end
    const prev = defaultMetricOptions || [];
    const preserved = prev.filter((v) => newOptions.includes(v));
    const appended = newOptions.filter((v) => !preserved.includes(v));
    const ordered = [...preserved, ...appended];

    dispatch({
      type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
      payload: { id, defaultMetricOptions: ordered },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (
      selectedMetrics.length > 0 &&
      (!defaultMetricOptions?.length ||
        !defaultMetricOptions?.some((opt) =>
          selectedMetrics.some((selected) => selected.value === opt)
        ))
    ) {
      dispatch({
        type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
        payload: {
          id,
          defaultMetricOptions: selectedMetrics.map((m) => m.value),
        },
        replacePreviousHistorySnapshot: true,
      });
    }
  }, [selectedMetrics, id]);

  const handleGroupSelectToggle = (
    groupLabel: string,
    groupOptions: readonly SelectableMetricOption[]
  ) => {
    const currentSelectedValues = selectedMetrics.map((m) => m.value);
    const groupValues = groupOptions.map((opt) => opt.value);

    const hasSelectedFromGroup = groupValues.some((val) =>
      currentSelectedValues.includes(val)
    );

    if (hasSelectedFromGroup) {
      const newSelection = selectedMetrics.filter(
        (metric) => !groupValues.includes(metric.value)
      );
      handleChangeMetrics(newSelection);
    } else {
      const groupOptionsToAdd = groupOptions.filter(
        (opt) => !currentSelectedValues.includes(opt.value)
      );
      const newSelection = [...selectedMetrics, ...groupOptionsToAdd];
      handleChangeMetrics(newSelection);
    }
  };

  // Custom Group component that renders a clickable header and collapsible options
  const CollapsibleGroup = (
    props: GroupProps<
      SelectableMetricOption,
      true,
      GroupBase<SelectableMetricOption>
    >
  ) => {
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    const { data, children, innerProps } = props;

    const label = String(data.label);
    const groupOptions = data.options || [];

    const currentSelectedValues = selectedMetrics.map((m) => m.value);
    const groupValues = groupOptions.map((opt) => opt.value);
    const allFromGroupSelected =
      groupValues.length > 0 &&
      groupValues.every((val) => currentSelectedValues.includes(val));

    // Auto-collapse when all items are selected
    const defaultOpen = !allFromGroupSelected;
    const isOpen = openGroups[label] ?? defaultOpen;

    const toggle = () =>
      setOpenGroups((prev) => ({
        ...prev,
        [label]: !(prev[label] ?? defaultOpen),
      }));

    const hasSelectedFromGroup = groupValues.some((val) =>
      currentSelectedValues.includes(val)
    );
    const selectAllText = hasSelectedFromGroup ? 'Deselect All' : 'Select All';

    return (
      <Box {...innerProps}>
        <Flex
          px={2}
          py={1.5}
          align="center"
          justify="space-between"
          _hover={{
            '.select-all-text': {
              opacity: 1,
              visibility: 'visible',
            },
          }}
        >
          <Flex flex="1" cursor="pointer" align="center" onClick={toggle}>
            <Text fontSize="sm" fontWeight="600">
              {label}
            </Text>
            <Icon
              as={ChevronDownIcon}
              ml={2}
              transition="transform 0.2s"
              transform={isOpen ? 'rotate(180deg)' : undefined}
            />
          </Flex>

          {groupOptions.length > 1 && (
            <Text
              className="select-all-text"
              fontSize="xs"
              color="blue.600"
              cursor="pointer"
              fontWeight="500"
              opacity={0}
              visibility="hidden"
              transition="opacity 0.2s, visibility 0.2s"
              _hover={{ color: 'blue.800', textDecoration: 'underline' }}
              onClick={(e) => {
                e.stopPropagation();
                handleGroupSelectToggle(label, groupOptions);
              }}
            >
              {selectAllText}
            </Text>
          )}
        </Flex>

        {isOpen && <Box pl={2}>{children}</Box>}
      </Box>
    );
  };

  if (!availableMetricOptions || availableMetricOptions.length === 0) {
    return null;
  }

  const hasDisabledSelected = selectedMetrics.some((m) => m.isDisabled);
  const dominantGroupHint = hasDisabledSelected
    ? getIsolationHint(selectedMetrics)
    : undefined;

  return (
    <>
      {showUnitOfAnalysis && (
        <Box marginBottom="1.5rem">
          <Flex alignItems="center" justifyContent="space-between" mb="0.5rem">
            <Text fontSize="sm" fontWeight={600} opacity={isDisabled ? 0.6 : 1}>
              Unit of Analysis
            </Text>
          </Flex>
          <Box mb="0.75rem" maxW="268px">
            <ChakraSelect<UnitOption, false, GroupBase<UnitOption>>
              chakraStyles={BaseSelectStyles}
              isDisabled={isDisabled}
              isSearchable={false}
              isClearable={false}
              options={unitOptions}
              value={unitSelected}
              onChange={(opt: SingleValue<UnitOption>) =>
                setUnit((opt?.value as UnitOfAnalysis) ?? 'primary')
              }
            />
          </Box>
        </Box>
      )}
      <Box marginBottom="1.5rem">
        {effectiveUnit !== 'employees' && (
          <>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              mb="0.5rem"
            >
              <Text
                fontSize="sm"
                fontWeight={600}
                opacity={isDisabled ? 0.6 : 1}
              >
                {isMulti ? 'Variables' : 'Variable'}
              </Text>
            </Flex>

            <Flex>
              <ChakraSelect<
                SelectableMetricOption,
                true,
                GroupBase<SelectableMetricOption>
              >
                chakraStyles={MultiMetricSelectStyles}
                options={adjustedAvailableMetricOptions}
                value={selectedMetrics}
                onChange={handleChangeMetrics}
                isSearchable
                isDisabled={isDisabled}
                isMulti={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                isClearable={false}
                components={{
                  Group: CollapsibleGroup,
                }}
              />
            </Flex>
          </>
        )}
        {dominantGroupHint && (
          <Text mt={1} fontSize="xs" color="gray.500">
            {dominantGroupHint}
          </Text>
        )}
      </Box>
    </>
  );
};

// Custom styles for multi-metric selector that allows vertical expansion
const MultiMetricSelectStyles: ChakraStylesConfig<
  SelectableMetricOption,
  true,
  GroupBase<SelectableMetricOption>
> = {
  ...BaseSelectStyles,
  container: (provided) => ({
    ...provided,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start', // Changed from center to allow expansion
    padding: 0,
    gap: '8px',
    boxSizing: 'border-box',
    width: '268px',
    minHeight: '32px', // Changed from fixed height to minHeight
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '4px',
  }),
  control: (provided) => ({
    ...provided,
    minHeight: '32px',
    height: 'auto', // Changed from fixed height to auto
    background: 'transparent',
    borderWidth: 0,
    boxShadow: 'none',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  }),
  valueContainer: (provided) => ({
    ...provided,
    fontSize: '14px',
    padding: '4px 28px 4px 8px', // leave space on right for chevron
    minHeight: '24px', // Ensure minimum height for the value container
    flexWrap: 'wrap', // Allow wrapping of selected values
    gap: '4px', // Add gap between selected values
  }),
  multiValue: (provided, state) => ({
    ...provided,
    backgroundColor: state.data.isDisabled ? '#F7FAFC' : '#DEF5EE',
    border: state.data.isDisabled ? '1px solid #E2E8F0' : '1px solid #B8E6D9',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    maxWidth: '120px', // Limit width of individual tags
    opacity: state.data.isDisabled ? 0.6 : 1,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#1A5F3C',
    padding: '2px 6px',
    fontSize: '12px',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#1A5F3C',
    ':hover': {
      backgroundColor: '#B8E6D9',
      color: '#0F3D26',
    },
  }),
  option: (provided, state) => {
    let backgroundColor = 'white';
    let color = '#2D3748';

    if (state.isSelected) {
      backgroundColor = '#DEF5EE';
      color = '#1A5F3C';
    } else if (state.isDisabled) {
      backgroundColor = '#F7FAFC';
      color = '#A0AEC0';
    } else if (state.isFocused) {
      backgroundColor = '#E2E8F0';
    }

    return {
      ...provided,
      backgroundColor,
      color,
      cursor: state.isDisabled ? 'not-allowed' : 'default',
      opacity: state.isDisabled ? 0.6 : 1,
      fontWeight: state.isSelected ? '500' : 'normal',
    };
  },
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: '0 6px',
    alignSelf: 'center',
    marginLeft: 'auto', // keep indicators pinned to the right
    marginRight: 0,
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    paddingRight: '2px',
    paddingLeft: '0px',
    marginRight: 0,
  }),
};
